/**
 * Deal Detail Page - Phase 3
 * Single source of truth for deal decision-making
 * 8 Structured Sections: Header, Quick Actions, Analyzer Summary (read-only),
 * Assumptions (editable), Renovation Tracking, Market Comparables, Notes & History, Partner Tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Building,
  CurrencyEur,
  TrendUp,
  Wrench,
  Bank,
  SignOut as ExitIcon,
  Warning,
  CheckCircle,
  Clock,
  PencilSimple,
  Calculator,
  Users,
  NotePencil,
  ChartLine,
  Target,
  Lightning,
  CaretRight,
  ArrowsClockwise,
  FloppyDisk,
  X,
  House,
  Star,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState, StatusBadge } from '../components/common';
import { dealsApi, partnersApi, comparablesApi, analyzerApi, renovationApi, exitTrackerApi, intelligenceApi } from '../utils/api';
import {
  formatCurrency,
  formatPercent,
  formatArea,
  formatPricePerSqm,
  formatDate,
  getRoiColorClass,
  getProfitColorClass,
  getSeverityColor,
  getStatusConfig,
  getRiskConfig,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ============== DEAL COMPARABLES INTELLIGENCE COMPONENT ==============
const DealComparablesIntelligence = ({ dealId }) => {
  const [loading, setLoading] = React.useState(true);
  const [analysis, setAnalysis] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchAnalysis = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await comparablesApi.getDealAnalysis(dealId);
      setAnalysis(data);
    } catch (err) {
      console.error('Failed to fetch comp analysis:', err);
      setError('Could not load comparable analysis');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  React.useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  // Handle verify/exclude toggle actions
  const handleVerify = async (compId) => {
    try {
      await comparablesApi.verify(compId);
      toast.success('Comparable vérifié');
      fetchAnalysis();
    } catch (err) {
      toast.error('Échec de la vérification');
    }
  };

  const handleExclude = async (compId) => {
    try {
      await comparablesApi.exclude(compId, 'Manually excluded by analyst');
      toast.success('Comparable exclu de l\'analyse');
      fetchAnalysis();
    } catch (err) {
      toast.error('Échec de l\'exclusion');
    }
  };

  const handleInclude = async (compId) => {
    try {
      await comparablesApi.include(compId);
      toast.success('Comparable inclus dans l\'analyse');
      fetchAnalysis();
    } catch (err) {
      toast.error('Échec de l\'inclusion');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <ArrowsClockwise size={24} className="animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Chargement de l'analyse des comparables...</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !analysis) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={House}
            title="Aucune analyse de comparables disponible"
            description={error || "Ajoutez des comparables pour obtenir des informations sur le marché."}
          />
        </CardContent>
      </Card>
    );
  }

  const { 
    current_condition_range, renovated_exit_range, acquisition_gap, 
    exit_realism, comp_set_strength, recommendation, top_comps 
  } = analysis;

  return (
    <div className="space-y-6">
      {/* Header with Link to Full Page */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ChartLine size={20} />
            Market Comparable Intelligence
          </h3>
          <p className="text-sm text-muted-foreground">
            Decision-support data from {analysis.selected_comps_count} relevant comparables
          </p>
        </div>
        <Link to={`/comparables?deal=${dealId}`}>
          <Button variant="outline" size="sm">
            <House size={16} className="mr-1" />
            Full Analysis
            <CaretRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>

      {/* Market Ranges Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Current Condition Range */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ChartLine size={16} />
              Current Condition Range
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Low</span>
              <span className="font-tabular">{formatPricePerSqm(current_condition_range?.per_sqm_low)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Base</span>
              <span className="font-tabular">{formatPricePerSqm(current_condition_range?.per_sqm_base)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">High</span>
              <span className="font-tabular">{formatPricePerSqm(current_condition_range?.per_sqm_high)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-2 border-t">
              Based on {current_condition_range?.comp_count || 0} comparable properties
            </p>
          </CardContent>
        </Card>

        {/* Renovated Exit Range */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendUp size={16} />
              Renovated Exit Range
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Low</span>
              <span className="font-tabular">{formatPricePerSqm(renovated_exit_range?.per_sqm_low)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Base</span>
              <span className="font-tabular">{formatPricePerSqm(renovated_exit_range?.per_sqm_base)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">High</span>
              <span className="font-tabular">{formatPricePerSqm(renovated_exit_range?.per_sqm_high)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-2 border-t">
              Based on {renovated_exit_range?.comp_count || 0} renovated comps
            </p>
          </CardContent>
        </Card>

        {/* Recommendation */}
        <Card className={cn(
          "border-2",
          recommendation?.recommendation_type === 'strong_buy' && "border-emerald-300 bg-emerald-50/30",
          recommendation?.recommendation_type === 'overpriced' && "border-red-300 bg-red-50/30",
          recommendation?.recommendation_type === 'exit_aggressive' && "border-amber-300 bg-amber-50/30",
          recommendation?.recommendation_type === 'weak_comps' && "border-stone-300 bg-stone-50/30"
        )}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target size={16} />
              Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-sm">{recommendation?.headline}</p>
            <p className="text-xs text-muted-foreground mt-1">{recommendation?.details}</p>
            <div className="mt-3 pt-2 border-t space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Acquisition</span>
                <span className="font-tabular font-medium">{formatCurrency(recommendation?.max_acquisition_supported)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Confidence</span>
                <Badge variant="secondary" className="capitalize text-xs">{recommendation?.confidence_level}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gap Analysis Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Acquisition Gap */}
        <Card data-testid="acquisition-gap-deal">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Acquisition Gap Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Target Acquisition</p>
                <p className="text-lg font-bold font-tabular">{formatCurrency(acquisition_gap?.target_acquisition_price)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Market Value</p>
                <p className="text-lg font-bold font-tabular">{formatCurrency(acquisition_gap?.market_value_weighted)}</p>
              </div>
            </div>
            <div className={cn(
              "mt-3 p-2 rounded-lg text-center",
              acquisition_gap?.discount_percent > 10 && "bg-emerald-100",
              acquisition_gap?.discount_percent > 0 && acquisition_gap?.discount_percent <= 10 && "bg-lime-100",
              acquisition_gap?.discount_percent <= 0 && acquisition_gap?.discount_percent > -10 && "bg-amber-100",
              acquisition_gap?.discount_percent <= -10 && "bg-red-100"
            )}>
              <p className={cn(
                "text-lg font-bold",
                acquisition_gap?.discount_percent > 0 ? "text-emerald-700" : "text-red-700"
              )}>
                {acquisition_gap?.discount_percent > 0 ? '+' : ''}{acquisition_gap?.discount_percent?.toFixed(1)}%
              </p>
              <p className="text-xs capitalize">{acquisition_gap?.gap_label?.replace('_', ' ')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Exit Realism */}
        <Card data-testid="exit-realism-deal">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Exit Realism Check</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Assumed Exit</p>
                <p className="text-lg font-bold font-tabular">{formatPricePerSqm(exit_realism?.assumed_exit_per_sqm)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Comp-Supported</p>
                <p className="text-lg font-bold font-tabular">{formatPricePerSqm(exit_realism?.comp_supported_per_sqm)}</p>
              </div>
            </div>
            <div className={cn(
              "mt-3 p-2 rounded-lg text-center",
              exit_realism?.realism_label === 'conservative' && "bg-cyan-100",
              exit_realism?.realism_label === 'realistic' && "bg-emerald-100",
              exit_realism?.realism_label === 'optimistic' && "bg-amber-100",
              exit_realism?.realism_label === 'over_optimistic' && "bg-red-100"
            )}>
              <p className={cn(
                "text-lg font-bold capitalize",
                exit_realism?.realism_label === 'realistic' && "text-emerald-700",
                exit_realism?.realism_label === 'conservative' && "text-cyan-700",
                exit_realism?.realism_label === 'optimistic' && "text-amber-700",
                exit_realism?.realism_label === 'over_optimistic' && "text-red-700"
              )}>
                {exit_realism?.realism_label?.replace('_', ' ')}
              </p>
              <p className="text-xs text-muted-foreground">
                {exit_realism?.delta_percent > 0 ? '+' : ''}{exit_realism?.delta_percent?.toFixed(1)}% vs base
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comp Set Strength */}
      {comp_set_strength && (
        <Card data-testid="comp-set-strength-deal">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm">Comparable Set Strength</CardTitle>
              <Badge 
                variant="secondary" 
                className={cn(
                  "text-xs",
                  comp_set_strength.overall_strength === 'strong' && "bg-emerald-100 text-emerald-700",
                  comp_set_strength.overall_strength === 'medium' && "bg-amber-100 text-amber-700",
                  comp_set_strength.overall_strength === 'weak' && "bg-red-100 text-red-700"
                )}
              >
                {comp_set_strength.overall_strength?.toUpperCase()} ({comp_set_strength.score})
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 text-center text-sm">
              <div>
                <p className="text-lg sm:text-xl font-bold">{comp_set_strength.total_comp_count}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold text-emerald-600">{comp_set_strength.good_comp_count}</p>
                <p className="text-xs text-muted-foreground">Good</p>
              </div>
              <div>
                <p className="text-lg sm:text-xl font-bold">{comp_set_strength.sold_count}</p>
                <p className="text-xs text-muted-foreground">Sold</p>
              </div>
              <div className="hidden sm:block">
                <p className="text-xl font-bold">{comp_set_strength.average_recency_days}</p>
                <p className="text-xs text-muted-foreground">Avg Days</p>
              </div>
              <div className="hidden sm:block">
                <p className="text-xl font-bold capitalize">{comp_set_strength.geographic_concentration}</p>
                <p className="text-xs text-muted-foreground">Geo</p>
              </div>
            </div>
            {/* Mobile-only extra row */}
            <div className="sm:hidden grid grid-cols-2 gap-2 text-center text-sm mt-2 pt-2 border-t">
              <div>
                <p className="text-lg font-bold">{comp_set_strength.average_recency_days}</p>
                <p className="text-xs text-muted-foreground">Avg Days</p>
              </div>
              <div>
                <p className="text-lg font-bold capitalize">{comp_set_strength.geographic_concentration}</p>
                <p className="text-xs text-muted-foreground">Geo</p>
              </div>
            </div>
            {comp_set_strength.weaknesses?.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                <p className="text-xs font-medium text-amber-800">Weaknesses:</p>
                <ul className="text-xs text-amber-700 mt-1">
                  {comp_set_strength.weaknesses.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top 5 Relevant Comparables */}
      <Card data-testid="top-comps-deal">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Top 5 Relevant Comparables</CardTitle>
        </CardHeader>
        <CardContent>
          {top_comps && top_comps.length > 0 ? (
            <>
              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                {top_comps.slice(0, 5).map((sc, idx) => {
                  const comp = sc.comparable;
                  const relevance = sc.relevance;
                  const effectivePrice = comp.sold_price || comp.asking_price;
                  const pricePerSqm = comp.price_per_sqm || (effectivePrice && comp.surface_sqm > 0 ? effectivePrice / comp.surface_sqm : null);
                  
                  return (
                    <div 
                      key={comp.id || idx}
                      className={cn(
                        "p-3 border rounded-lg",
                        comp.excluded_from_analysis && "opacity-50 bg-muted/30"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{comp.label || comp.address?.slice(0, 30)}</p>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <span className="text-xs text-muted-foreground">{comp.arrondissement}</span>
                            {comp.source_import_id && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-violet-50 text-violet-600 border-violet-200">
                                Imported
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className={cn(
                          "text-lg font-bold px-2 py-1 rounded",
                          relevance?.total_score >= 80 && "text-emerald-600 bg-emerald-50",
                          relevance?.total_score >= 65 && relevance?.total_score < 80 && "text-lime-600 bg-lime-50",
                          relevance?.total_score >= 50 && relevance?.total_score < 65 && "text-amber-600 bg-amber-50",
                          relevance?.total_score < 50 && "text-red-600 bg-red-50"
                        )}>
                          {relevance?.total_score?.toFixed(0)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type:</span>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "text-[10px]",
                              comp.observation_type === 'sold' && "bg-emerald-100 text-emerald-700",
                              comp.observation_type === 'asking' && "bg-amber-100 text-amber-700"
                            )}
                          >
                            {comp.observation_type === 'sold' ? 'Sold' : 'Asking'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Surface:</span>
                          <span className="font-tabular">{formatArea(comp.surface_sqm)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Price:</span>
                          <span className="font-tabular font-medium">{formatCurrency(effectivePrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">€/m²:</span>
                          <span className="font-tabular">{formatPricePerSqm(pricePerSqm)}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {comp.renovation_state?.replace('_', ' ')}
                        </Badge>
                        <div className="flex items-center gap-1">
                          {!comp.manually_verified && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleVerify(comp.id)} title="Verify">
                              <CheckCircle size={14} />
                            </Button>
                          )}
                          {comp.manually_verified && <CheckCircle size={14} className="text-emerald-600" title="Verified" />}
                          {!comp.excluded_from_analysis ? (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleExclude(comp.id)} title="Exclude">
                              <Warning size={14} className="text-amber-600" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleInclude(comp.id)} title="Include">
                              <CheckCircle size={14} className="text-muted-foreground" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: Table Layout */}
              <Table className="hidden md:table">
                <TableHeader>
                  <TableRow className="bg-secondary/20">
                    <TableHead>Address</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Surface</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">€/m²</TableHead>
                    <TableHead className="text-center">Relevance</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_comps.slice(0, 5).map((sc, idx) => {
                    const comp = sc.comparable;
                    const relevance = sc.relevance;
                    const effectivePrice = comp.sold_price || comp.asking_price;
                    const pricePerSqm = comp.price_per_sqm || (effectivePrice && comp.surface_sqm > 0 ? effectivePrice / comp.surface_sqm : null);
                    
                    return (
                      <TableRow 
                        key={comp.id || idx}
                        className={cn(comp.excluded_from_analysis && "opacity-50")}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{comp.label || comp.address?.slice(0, 30)}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-xs text-muted-foreground">{comp.arrondissement}</span>
                              {comp.source_import_id && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-violet-50 text-violet-600 border-violet-200">
                                  Imported
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "text-xs",
                              comp.observation_type === 'sold' && "bg-emerald-100 text-emerald-700",
                              comp.observation_type === 'asking' && "bg-amber-100 text-amber-700"
                            )}
                          >
                            {comp.observation_type === 'sold' ? 'Sold' : 'Asking'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-tabular">{formatArea(comp.surface_sqm)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs capitalize">
                            {comp.renovation_state?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-tabular font-medium">
                          {formatCurrency(effectivePrice)}
                        </TableCell>
                        <TableCell className="text-right font-tabular">
                          {formatPricePerSqm(pricePerSqm)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className={cn(
                            "text-sm font-bold",
                            relevance?.total_score >= 80 && "text-emerald-600",
                            relevance?.total_score >= 65 && relevance?.total_score < 80 && "text-lime-600",
                            relevance?.total_score >= 50 && relevance?.total_score < 65 && "text-amber-600",
                            relevance?.total_score < 50 && "text-red-600"
                          )}>
                            {relevance?.total_score?.toFixed(0)}
                          </div>
                          <p className="text-xs text-muted-foreground">{relevance?.quality_label}</p>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {!comp.manually_verified && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => handleVerify(comp.id)}
                                title="Vérifier"
                              >
                                <CheckCircle size={14} />
                              </Button>
                            )}
                            {comp.manually_verified && (
                              <CheckCircle size={14} className="text-emerald-600" title="Verified" />
                            )}
                            {!comp.excluded_from_analysis ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => handleExclude(comp.id)}
                                title="Exclude"
                              >
                                <Warning size={14} className="text-amber-600" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => handleInclude(comp.id)}
                                title="Inclure"
                              >
                                <CheckCircle size={14} className="text-muted-foreground" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          ) : (
            <EmptyState
              icon={House}
              title="Aucun comparable pertinent"
              description="Ajoutez des comparables pour cette zone pour obtenir des informations sur le marché."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== DEAL AI INTELLIGENCE SUMMARY COMPONENT ==============
const DealAIIntelligenceSummary = ({ dealId }) => {
  const [loading, setLoading] = React.useState(true);
  const [intel, setIntel] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchIntelligence = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await intelligenceApi.getSummary(dealId);
      setIntel(data);
    } catch (err) {
      console.error('Failed to fetch intelligence:', err);
      setError('Could not load intelligence');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  React.useEffect(() => {
    fetchIntelligence();
  }, [fetchIntelligence]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <ArrowsClockwise size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !intel) {
    return null; // Silently fail - AI intelligence is supplementary
  }

  const SCORE_CONFIG = {
    excellent: { color: 'text-emerald-600', bg: 'bg-emerald-100' },
    good: { color: 'text-blue-600', bg: 'bg-blue-100' },
    average: { color: 'text-amber-600', bg: 'bg-amber-100' },
    weak: { color: 'text-orange-600', bg: 'bg-orange-100' },
    poor: { color: 'text-red-600', bg: 'bg-red-100' },
  };

  const ACTION_CONFIG = {
    strong_buy: { color: 'bg-emerald-500 text-white' },
    buy_below_target: { color: 'bg-blue-500 text-white' },
    negotiate: { color: 'bg-amber-500 text-white' },
    monitor: { color: 'bg-slate-500 text-white' },
    high_risk: { color: 'bg-orange-500 text-white' },
    reject: { color: 'bg-red-500 text-white' },
  };

  const scoreConfig = SCORE_CONFIG[intel.deal_score_label] || SCORE_CONFIG.average;
  const actionConfig = ACTION_CONFIG[intel.recommendation?.action] || ACTION_CONFIG.monitor;

  return (
    <Card className="border-2 border-[#3A4A40]" data-testid="ai-intelligence-summary">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Lightning size={18} className="text-[#3A4A40]" />
            AI Intelligence Summary
          </span>
          <Link to={`/intelligence?deal=${dealId}`}>
            <Button variant="ghost" size="sm" className="gap-1 h-7">
              Full Analysis
              <CaretRight size={14} />
            </Button>
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 bg-secondary/30 rounded-lg">
            <p className="text-xs text-muted-foreground">Deal Score</p>
            <p className={cn('text-2xl font-bold', scoreConfig.color)}>{intel.deal_score?.toFixed(0)}</p>
            <Badge className={cn('mt-1 capitalize', scoreConfig.bg, scoreConfig.color)}>
              {intel.deal_score_label}
            </Badge>
          </div>
          <div className="text-center p-3 bg-secondary/30 rounded-lg">
            <p className="text-xs text-muted-foreground">Risk</p>
            <p className={cn(
              'text-2xl font-bold',
              intel.risk_score >= 50 ? 'text-red-600' : 'text-emerald-600'
            )}>{intel.risk_score?.toFixed(0)}</p>
            <Badge className={cn('mt-1 capitalize', 
              intel.risk_level === 'low' && 'bg-emerald-100 text-emerald-700',
              intel.risk_level === 'medium' && 'bg-amber-100 text-amber-700',
              intel.risk_level === 'high' && 'bg-orange-100 text-orange-700',
              intel.risk_level === 'critical' && 'bg-red-100 text-red-700'
            )}>
              {intel.risk_level}
            </Badge>
          </div>
          <div className="text-center p-3 bg-secondary/30 rounded-lg">
            <p className="text-xs text-muted-foreground">Confidence</p>
            <p className="text-2xl font-bold">{intel.confidence_score?.toFixed(0)}</p>
            <Badge className="mt-1 capitalize bg-secondary text-foreground">
              {intel.confidence_level}
            </Badge>
          </div>
          <div className="text-center p-3 rounded-lg border-2" style={{ borderColor: '#3A4A40' }}>
            <p className="text-xs text-muted-foreground">Recommendation</p>
            <Badge className={cn('mt-2 text-sm font-bold', actionConfig.color)}>
              {intel.recommendation?.action_label}
            </Badge>
          </div>
        </div>

        {/* Headline */}
        <p className="text-sm font-medium mb-2">{intel.recommendation?.headline}</p>

        {/* Top Flags */}
        {intel.top_flags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {intel.top_flags.map((flag, i) => (
              <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                <Warning size={12} className="mr-1" />
                {flag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== DEAL EXIT INTELLIGENCE COMPONENT ==============
const DealExitIntelligence = ({ dealId }) => {
  const [loading, setLoading] = React.useState(true);
  const [tracker, setTracker] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchTracker = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await exitTrackerApi.getTracker(dealId);
      setTracker(data);
    } catch (err) {
      console.error('Failed to fetch exit tracker:', err);
      setError('Could not load exit intelligence');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  React.useEffect(() => {
    fetchTracker();
  }, [fetchTracker]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <ArrowsClockwise size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !tracker) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={Warning}
            title="Données de Sortie Indisponibles"
            description={error || "Impossible de charger l'intelligence de sortie"}
          />
        </CardContent>
      </Card>
    );
  }

  const STATUS_COLORS = {
    not_listed: 'bg-stone-100 text-stone-700',
    listed: 'bg-blue-100 text-blue-700',
    under_offer: 'bg-amber-100 text-amber-700',
    sold: 'bg-emerald-100 text-emerald-700',
  };

  const URGENCY_COLORS = {
    low: 'bg-emerald-100 text-emerald-700',
    medium: 'bg-amber-100 text-amber-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-6" data-testid="exit-intelligence-deal">
      {/* Key Exit Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">Status</p>
            <Badge className={cn('mt-1 capitalize text-xs', STATUS_COLORS[tracker.listing_status])}>
              {tracker.listing_status?.replace('_', ' ')}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">Days on Market</p>
            <p className="text-lg md:text-xl font-bold font-tabular">{tracker.days_on_market}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">Current Price</p>
            <p className="text-lg md:text-xl font-bold font-tabular">
              {formatCurrency(tracker.current_asking_price || tracker.target_exit_price)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">Net Profit</p>
            <p className={cn(
              "text-lg md:text-xl font-bold font-tabular",
              tracker.net_profit_at_current_price >= 0 ? "text-emerald-600" : "text-red-600"
            )}>
              {formatCurrency(tracker.net_profit_at_current_price)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">ROI</p>
            <p className={cn(
              "text-lg md:text-xl font-bold",
              tracker.roi_at_current_price >= 15 ? "text-emerald-600" : 
              tracker.roi_at_current_price >= 10 ? "text-amber-600" : "text-red-600"
            )}>
              {tracker.roi_at_current_price?.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <p className="text-xs text-muted-foreground">Urgency</p>
            <Badge className={cn('mt-1 capitalize text-xs', URGENCY_COLORS[tracker.time_impact?.urgency_level || 'low'])}>
              {tracker.time_impact?.urgency_level || 'Low'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Recommendation & Pricing Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recommendation */}
        {tracker.recommendation && (
          <Card className="border-2 border-[#3A4A40] bg-[#F8FAF9]" data-testid="exit-recommendation-deal">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Lightning size={18} className="text-[#3A4A40]" />
                Recommended Action
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h3 className="text-lg font-bold text-[#1C2B24]">{tracker.recommendation.headline}</h3>
              <p className="text-sm text-muted-foreground mt-1">{tracker.recommendation.details}</p>
              
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="p-2 bg-white rounded-lg border text-center">
                  <p className="text-xs text-muted-foreground">Optimal</p>
                  <p className="font-bold font-tabular text-sm">{formatCurrency(tracker.recommendation.optimal_listing_price)}</p>
                </div>
                <div className="p-2 bg-white rounded-lg border text-center">
                  <p className="text-xs text-muted-foreground">Minimum</p>
                  <p className="font-bold font-tabular text-sm">{formatCurrency(tracker.recommendation.minimum_acceptable_price)}</p>
                </div>
                <div className="p-2 bg-white rounded-lg border text-center">
                  <p className="text-xs text-muted-foreground">Break-Even</p>
                  <p className="font-bold font-tabular text-sm">{formatCurrency(tracker.recommendation.break_even_price)}</p>
                </div>
              </div>
              
              {tracker.recommendation.action_items?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium mb-1">Action Items:</p>
                  <ul className="space-y-0.5">
                    {tracker.recommendation.action_items.slice(0, 3).map((item, i) => (
                      <li key={i} className="flex items-start gap-1 text-xs text-muted-foreground">
                        <CaretRight size={12} className="mt-0.5 text-[#3A4A40]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Key Price Levels */}
        <Card data-testid="exit-prices-deal">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CurrencyEur size={18} />
              Key Price Levels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 bg-secondary/30 rounded-lg">
                <span className="text-sm">Target Exit Price</span>
                <span className="font-bold font-tabular text-emerald-700">
                  {formatCurrency(tracker.target_exit_price)}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-amber-50 rounded-lg border border-amber-200">
                <span className="text-sm">Minimum Acceptable</span>
                <span className="font-bold font-tabular text-amber-700">
                  {formatCurrency(tracker.minimum_acceptable_price)}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-red-50 rounded-lg border border-red-200">
                <span className="text-sm">Break-Even</span>
                <span className="font-bold font-tabular text-red-700">
                  {formatCurrency(tracker.break_even_price)}
                </span>
              </div>
              {tracker.comp_supported_range && (
                <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm">Comp-Supported</span>
                  <span className="font-bold font-tabular text-blue-700">
                    {formatCurrency(tracker.comp_supported_range.base)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Best Offer & Time Impact Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Offer */}
        <Card data-testid="best-offer-deal">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users size={18} />
              Best Current Offer
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tracker.best_offer ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold font-tabular">
                    {formatCurrency(tracker.best_offer.offer_price)}
                  </p>
                  <Badge 
                    className={cn(
                      "capitalize",
                      tracker.best_offer.recommendation === 'accept' && "bg-emerald-100 text-emerald-700",
                      tracker.best_offer.recommendation === 'negotiate' && "bg-amber-100 text-amber-700",
                      tracker.best_offer.recommendation === 'reject' && "bg-red-100 text-red-700",
                      tracker.best_offer.recommendation === 'wait' && "bg-blue-100 text-blue-700"
                    )}
                  >
                    {tracker.best_offer.recommendation}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Net Profit</p>
                    <p className={cn(
                      "font-bold font-tabular",
                      tracker.best_offer.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {formatCurrency(tracker.best_offer.net_profit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">ROI</p>
                    <p className="font-bold">{tracker.best_offer.roi_percent?.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">vs Target</p>
                    <p className={cn(
                      "font-bold",
                      tracker.best_offer.delta_vs_target_percent >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {tracker.best_offer.delta_vs_target_percent > 0 ? '+' : ''}{tracker.best_offer.delta_vs_target_percent?.toFixed(1)}%
                    </p>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground italic">
                  {tracker.best_offer.recommendation_reason}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-24 text-muted-foreground">
                <p className="text-sm">Aucune offre reçue pour le moment</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Time Impact */}
        {tracker.time_impact && (
          <Card data-testid="time-impact-deal">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock size={18} />
                Time Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Monthly Holding Cost</p>
                    <p className="text-xl font-bold font-tabular text-red-600">
                      -{formatCurrency(tracker.time_impact.monthly_holding_cost)}/mo
                    </p>
                  </div>
                  <Badge className={cn('capitalize', URGENCY_COLORS[tracker.time_impact.urgency_level])}>
                    {tracker.time_impact.urgency_level}
                  </Badge>
                </div>
                
                <p className="text-xs text-muted-foreground">{tracker.time_impact.urgency_reason}</p>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-secondary/30 rounded">
                    <p className="text-xs text-muted-foreground">+30d cost</p>
                    <p className="font-tabular text-sm text-red-600">-{formatCurrency(tracker.time_impact.cost_if_wait_30_days)}</p>
                  </div>
                  <div className="p-2 bg-secondary/30 rounded">
                    <p className="text-xs text-muted-foreground">+60d cost</p>
                    <p className="font-tabular text-sm text-red-600">-{formatCurrency(tracker.time_impact.cost_if_wait_60_days)}</p>
                  </div>
                  <div className="p-2 bg-secondary/30 rounded">
                    <p className="text-xs text-muted-foreground">+90d cost</p>
                    <p className="font-tabular text-sm text-red-600">-{formatCurrency(tracker.time_impact.cost_if_wait_90_days)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Offers Table */}
      {tracker.offers && tracker.offers.length > 0 && (
        <Card data-testid="offers-table-deal">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              All Offers ({tracker.offers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mobile: Card layout */}
            <div className="md:hidden space-y-3">
              {tracker.offers.slice(0, 5).map((offer) => (
                <div key={offer.offer_id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold font-tabular">{formatCurrency(offer.offer_price)}</span>
                    <Badge className={cn(
                      "capitalize text-xs",
                      offer.recommendation === 'accept' && "bg-emerald-100 text-emerald-700",
                      offer.recommendation === 'negotiate' && "bg-amber-100 text-amber-700",
                      offer.recommendation === 'reject' && "bg-red-100 text-red-700"
                    )}>
                      {offer.recommendation}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground block">Net Profit</span>
                      <span className={cn("font-tabular font-medium", offer.net_profit >= 0 ? "text-emerald-600" : "text-red-600")}>
                        {formatCurrency(offer.net_profit)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">ROI</span>
                      <span className="font-tabular">{offer.roi_percent?.toFixed(1)}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">vs Target</span>
                      <span className={cn("font-tabular", offer.delta_vs_target_percent >= 0 ? "text-emerald-600" : "text-red-600")}>
                        {offer.delta_vs_target_percent > 0 ? '+' : ''}{offer.delta_vs_target_percent?.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="bg-secondary/30">
                  <TableHead>Price</TableHead>
                  <TableHead>Net Profit</TableHead>
                  <TableHead>ROI</TableHead>
                  <TableHead>vs Target</TableHead>
                  <TableHead>Recommendation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tracker.offers.slice(0, 5).map((offer) => (
                  <TableRow key={offer.offer_id}>
                    <TableCell className="font-bold font-tabular">
                      {formatCurrency(offer.offer_price)}
                    </TableCell>
                    <TableCell className={cn(
                      "font-tabular",
                      offer.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {formatCurrency(offer.net_profit)}
                    </TableCell>
                    <TableCell className="font-tabular">{offer.roi_percent?.toFixed(1)}%</TableCell>
                    <TableCell className={cn(
                      "font-tabular",
                      offer.delta_vs_target_percent >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {offer.delta_vs_target_percent > 0 ? '+' : ''}{offer.delta_vs_target_percent?.toFixed(1)}%
                    </TableCell>
                    <TableCell>
                      <Badge className={cn(
                        "capitalize",
                        offer.recommendation === 'accept' && "bg-emerald-100 text-emerald-700",
                        offer.recommendation === 'negotiate' && "bg-amber-100 text-amber-700",
                        offer.recommendation === 'reject' && "bg-red-100 text-red-700"
                      )}>
                        {offer.recommendation}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Link to full Exit Tracker */}
      <div className="flex justify-end">
        <Link to={`/exit?deal=${dealId}`}>
          <Button variant="outline" className="gap-2">
            View Full Exit Tracker
            <CaretRight size={16} />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// ============== DEAL PARTNER INTELLIGENCE COMPONENT ==============
const DealPartnerIntelligence = ({ dealId }) => {
  const [loading, setLoading] = React.useState(true);
  const [partnerData, setPartnerData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchPartners = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnersApi.getDealPartners(dealId);
      setPartnerData(data);
    } catch (err) {
      console.error('Failed to fetch deal partners:', err);
      setError('Could not load partner data');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  React.useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <ArrowsClockwise size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={Warning}
            title="Données Partenaire Indisponibles"
            description={error}
          />
        </CardContent>
      </Card>
    );
  }

  const SCORE_LABELS = {
    top_performer: { label: 'Top Performer', color: 'bg-emerald-100 text-emerald-700' },
    strong: { label: 'Strong', color: 'bg-blue-100 text-blue-700' },
    average: { label: 'Average', color: 'bg-amber-100 text-amber-700' },
    weak: { label: 'Weak', color: 'bg-orange-100 text-orange-700' },
    high_risk: { label: 'High Risk', color: 'bg-red-100 text-red-700' },
  };

  const PartnerCard = ({ partner, title }) => {
    if (!partner) {
      return (
        <Card className="border-dashed border-2 bg-secondary/10">
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <Users size={32} className="mx-auto mb-2 opacity-50" />
              <p>No {title.toLowerCase()} assigned</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    const scoreConfig = SCORE_LABELS[partner.score_label] || SCORE_LABELS.average;

    return (
      <Card data-testid={`partner-card-${partner.partner_id}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{title}</span>
            <Badge className={cn(scoreConfig.color)}>
              {scoreConfig.label}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <Users size={24} className="text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{partner.name}</h3>
              {partner.company && (
                <p className="text-sm text-muted-foreground">{partner.company}</p>
              )}
              <p className="text-xs text-muted-foreground capitalize">{partner.role?.replace('_', ' ')}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <Star size={16} className="text-amber-500" />
                <span className={cn(
                  "text-xl font-bold",
                  partner.score >= 70 ? "text-emerald-600" :
                  partner.score >= 50 ? "text-amber-600" : "text-red-600"
                )}>
                  {partner.score?.toFixed(0)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{partner.total_deals} deals</p>
            </div>
          </div>
          
          {/* Performance Metrics */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="p-2 bg-secondary/30 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">
                {title === 'Deal Sourcer' ? 'Fee' : 'Fee'}
              </p>
              <p className="font-bold font-tabular text-sm">
                {formatPercent(partner.sourcing_fee_percent || partner.project_management_fee_percent || 0)}
              </p>
            </div>
            <div className="p-2 bg-secondary/30 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">Avg ROI</p>
              <p className={cn(
                "font-bold font-tabular text-sm",
                partner.avg_roi_percent >= 15 ? "text-emerald-600" :
                partner.avg_roi_percent >= 10 ? "text-amber-600" : "text-red-600"
              )}>
                {partner.avg_roi_percent?.toFixed(1)}%
              </p>
            </div>
            <div className="p-2 bg-secondary/30 rounded-lg text-center">
              <p className="text-xs text-muted-foreground">
                {title === 'Deal Sourcer' ? 'Accuracy' : 'Adherence'}
              </p>
              <p className="font-bold font-tabular text-sm">
                {title === 'Deal Sourcer' 
                  ? `${partner.forecast_accuracy?.toFixed(0) || '-'}%`
                  : `${partner.budget_adherence_rate?.toFixed(0) || '-'}%`
                }
              </p>
            </div>
          </div>
          
          {/* Additional Info for PM */}
          {title === 'Project Manager' && partner.avg_delay_days !== undefined && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Avg Delay:</span>
              <span className={cn(
                "font-medium",
                partner.avg_delay_days <= 0 ? "text-emerald-600" :
                partner.avg_delay_days <= 14 ? "text-amber-600" : "text-red-600"
              )}>
                {partner.avg_delay_days > 0 ? '+' : ''}{partner.avg_delay_days} days
              </span>
            </div>
          )}
          
          {/* Link to full scorecard */}
          <div className="mt-4 pt-4 border-t">
            <Link to="/partners">
              <Button variant="outline" size="sm" className="w-full gap-2">
                View Full Scorecard
                <CaretRight size={16} />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="partner-intelligence-deal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PartnerCard partner={partnerData?.sourcer} title="Sourceur" />
        <PartnerCard partner={partnerData?.project_manager} title="Chef de Projet" />
      </div>
    </div>
  );
};

// Stage transition options based on current stage
const getNextStages = (currentStage) => {
  const transitions = {
    sourcing: ['initial_screening', 'dead'],
    initial_screening: ['underwriting', 'dead'],
    underwriting: ['offer_made', 'dead'],
    offer_made: ['under_negotiation', 'dead'],
    under_negotiation: ['acquired', 'dead'],
    acquired: ['renovation'],
    renovation: ['ready_for_sale'],
    ready_for_sale: ['sold'],
    sold: [],
    dead: ['sourcing'], // Can reactivate
  };
  return transitions[currentStage] || [];
};

export const DealDetail = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deal, setDeal] = useState(null);
  const [partners, setPartners] = useState([]);
  const [comparables, setComparables] = useState([]);
  const [analyzerResults, setAnalyzerResults] = useState(null);
  const [error, setError] = useState(null);
  
  // Edit mode states
  const [editingAssumptions, setEditingAssumptions] = useState(false);
  const [assumptions, setAssumptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dealData, partnersData, comparablesData] = await Promise.all([
        dealsApi.getById(dealId),
        partnersApi.getAll(),
        comparablesApi.getAll({ deal_id: dealId }),
      ]);
      setDeal(dealData);
      setPartners(partnersData);
      setComparables(comparablesData);
      
      // Initialize assumptions from deal data
      setAssumptions({
        target_purchase_price: dealData.acquisition?.target_purchase_price || 0,
        notary_costs_percent: dealData.acquisition?.notary_costs_percent || 7.5,
        agency_fee_percent: dealData.acquisition?.agency_fee_percent || 0,
        renovation_budget: dealData.renovation?.renovation_budget || 0,
        contingency_percent: dealData.renovation?.contingency_percent || 10,
        design_styling_budget: dealData.renovation?.design_styling_budget || 0,
        project_management_fee: dealData.renovation?.project_management_fee || 0,
        holding_period_months: dealData.financing?.holding_period_months || 12,
        interest_rate_annual: dealData.financing?.interest_rate_annual || 0,
        target_sale_price: dealData.exit_assumptions?.target_sale_price || 0,
        expected_sales_fee_percent: dealData.exit_assumptions?.expected_sales_fee_percent || 3.0,
        target_roi_percent: dealData.target_roi_percent || 20,
      });
    } catch (err) {
      console.error('Failed to fetch deal:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Run analyzer on deal
  const runAnalyzer = useCallback(async () => {
    if (!deal) return;
    
    try {
      setAnalyzing(true);
      const result = await analyzerApi.analyzeExistingDeal(dealId, assumptions?.target_roi_percent || 20);
      setAnalyzerResults(result.analysis);
      toast.success('Analyse terminée');
    } catch (err) {
      console.error('Analyzer failed:', err);
      toast.error('Échec de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  }, [deal, dealId, assumptions?.target_roi_percent]);

  // Save assumptions and recalculate
  const saveAssumptions = async () => {
    try {
      setSaving(true);
      
      // Build update payload
      const updatePayload = {
        acquisition: {
          ...deal.acquisition,
          target_purchase_price: assumptions.target_purchase_price,
          notary_costs_percent: assumptions.notary_costs_percent,
          agency_fee_percent: assumptions.agency_fee_percent,
        },
        renovation: {
          ...deal.renovation,
          renovation_budget: assumptions.renovation_budget,
          contingency_percent: assumptions.contingency_percent,
          design_styling_budget: assumptions.design_styling_budget,
          project_management_fee: assumptions.project_management_fee,
        },
        financing: {
          ...deal.financing,
          holding_period_months: assumptions.holding_period_months,
          interest_rate_annual: assumptions.interest_rate_annual,
        },
        exit_assumptions: {
          ...deal.exit_assumptions,
          target_sale_price: assumptions.target_sale_price,
          expected_sales_fee_percent: assumptions.expected_sales_fee_percent,
        },
        target_roi_percent: assumptions.target_roi_percent,
      };
      
      await dealsApi.update(dealId, updatePayload);
      await fetchData(); // Refresh deal data
      await runAnalyzer(); // Re-run analyzer
      
      setEditingAssumptions(false);
      toast.success('Hypothèses enregistrées');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Échec de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Update deal status
  const updateStatus = async (newStatus) => {
    try {
      await dealsApi.update(dealId, { status: newStatus });
      await fetchData();
      toast.success(`Statut mis à jour: ${getStatusConfig(newStatus).label}`);
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error('Échec de la mise à jour du statut');
    }
  };

  if (loading) return <LoadingPage message="Chargement des détails du deal..." />;

  if (error || !deal) {
    return (
      <EmptyState
        icon={Warning}
        title="Deal introuvable"
        description={error || "Le deal demandé est introuvable."}
        actionLabel="Back to Pipeline"
        onAction={() => window.history.back()}
      />
    );
  }

  const assignedPartner = partners.find(p => p.id === deal.assigned_partner_id);
  const sourcedByPartner = partners.find(p => p.id === deal.sourced_by_id);
  const riskConfig = getRiskConfig(deal.risk_level, deal.risk_score);
  const nextStages = getNextStages(deal.status);

  // Calculate milestone progress
  const completedMilestones = deal.milestones?.filter(m => m.completed).length || 0;
  const totalMilestones = deal.milestones?.length || 0;
  const milestoneProgress = totalMilestones > 0 
    ? (completedMilestones / totalMilestones) * 100 
    : 0;

  return (
    <div data-testid="deal-detail" className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link to="/pipeline">
        <Button variant="ghost" size="sm" data-testid="back-to-pipeline">
          <ArrowLeft size={16} className="mr-1" />
          Back to Pipeline
        </Button>
      </Link>

      {/* ===== SECTION 1: HEADER ===== */}
      <div className="card-premium" data-testid="deal-header">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 flex-1">
            {/* Title row - stack badges on mobile */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-xl md:text-2xl font-bold">{deal.deal_name}</h1>
              <StatusBadge status={deal.status} />
              {deal.risk_level && (
                <Badge variant="secondary" className={cn("text-xs", riskConfig.color)}>
                  {riskConfig.label}
                </Badge>
              )}
            </div>
            {/* Property info - grid on mobile, inline on desktop */}
            <div className="hidden md:flex items-center gap-2 text-muted-foreground text-sm">
              <MapPin size={16} />
              <span>{deal.property.address}</span>
              <span>|</span>
              <span>{deal.property.arrondissement} Arr.</span>
              <span>|</span>
              <span>{deal.property.postal_code}</span>
              <span>|</span>
              <Building size={16} />
              <span className="capitalize">{deal.property.property_type}</span>
              <span>|</span>
              <span>{formatArea(deal.property.surface_sqm)}</span>
            </div>
            {/* Mobile property info - stacked layout */}
            <div className="md:hidden space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="flex-shrink-0" />
                <span className="truncate">{deal.property.address}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span>{deal.property.arrondissement} Arr.</span>
                <span>•</span>
                <span>{deal.property.postal_code}</span>
                <span>•</span>
                <span className="capitalize">{deal.property.property_type}</span>
                <span>•</span>
                <span>{formatArea(deal.property.surface_sqm)}</span>
              </div>
            </div>
            {deal.tags && deal.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {deal.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* ===== SECTION 2: QUICK ACTIONS PANEL ===== */}
          <div className="flex flex-col gap-2" data-testid="quick-actions">
            {/* Mobile: full width buttons stacked, Desktop: inline */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => navigate(`/deal/${dealId}/edit`)}
                data-testid="edit-deal-btn"
              >
                <PencilSimple size={16} className="mr-1" />
                Modifier Deal
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setEditingAssumptions(true)}
                data-testid="edit-assumptions-btn"
              >
                <PencilSimple size={16} className="mr-1" />
                Édition Rapide
              </Button>
              <Button 
                size="sm"
                className="bg-[#3A4A40] hover:bg-[#2E3B33] w-full sm:w-auto"
                onClick={runAnalyzer}
                disabled={analyzing}
                data-testid="run-analyzer-btn"
              >
                {analyzing ? (
                  <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                ) : (
                  <Calculator size={16} className="mr-1" />
                )}
                {analyzing ? 'Analyse...' : 'Lancer Analyseur'}
              </Button>
            </div>
            
            {/* Stage Transitions - scrollable on mobile */}
            {nextStages.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Passer à:</span>
                {nextStages.map(stage => (
                  <Button
                    key={stage}
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap flex-shrink-0"
                    onClick={() => updateStatus(stage)}
                    data-testid={`stage-btn-${stage}`}
                  >
                    <CaretRight size={14} className="mr-1" />
                    {getStatusConfig(stage).label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Row - Read Only from Backend */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Surface</p>
          <p className="text-lg font-bold font-tabular">{formatArea(deal.property.surface_sqm)}</p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Coût Projet</p>
          <p className="text-lg font-bold font-tabular">{formatCurrency(deal.total_project_cost)}</p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Coût/m²</p>
          <p className="text-lg font-bold font-tabular">{formatPricePerSqm(deal.cost_per_sqm)}</p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Profit Attendu</p>
          <p className={cn('text-lg font-bold font-tabular', getProfitColorClass(deal.expected_gross_profit))}>
            {formatCurrency(deal.expected_gross_profit)}
          </p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Rendement Attendu</p>
          <p className={cn('text-lg font-bold font-tabular', getRoiColorClass(deal.expected_roi_percent))}>
            {formatPercent(deal.expected_roi_percent)}
          </p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Seuil Rentabilité</p>
          <p className="text-lg font-bold font-tabular">{formatCurrency(deal.break_even_exit_price)}</p>
        </div>
        <div className="card-premium text-center py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Offre Max</p>
          <p className="text-lg font-bold font-tabular text-primary">{formatCurrency(deal.max_bid_at_target_roi)}</p>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="analyzer" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList data-testid="deal-tabs" className="inline-flex w-max md:w-auto md:flex-wrap">
            <TabsTrigger value="analyzer" className="gap-1 whitespace-nowrap">
              <Calculator size={16} />
              <span className="hidden sm:inline">Résumé Analyse</span>
              <span className="sm:hidden">Analyse</span>
            </TabsTrigger>
            <TabsTrigger value="intelligence" className="gap-1 whitespace-nowrap">
              <Lightning size={16} />
              <span className="hidden sm:inline">Intelligence</span>
              <span className="sm:hidden">Intel</span>
            </TabsTrigger>
            <TabsTrigger value="assumptions" className="gap-1 whitespace-nowrap">
              <Target size={16} />
              <span className="hidden sm:inline">Hypothèses</span>
              <span className="sm:hidden">Hypo</span>
            </TabsTrigger>
            <TabsTrigger value="renovation" className="gap-1 whitespace-nowrap">
              <Wrench size={16} />
              <span className="hidden sm:inline">Rénovation</span>
              <span className="sm:hidden">Réno</span>
            </TabsTrigger>
            <TabsTrigger value="comparables" className="gap-1 whitespace-nowrap">
              <ChartLine size={16} />
              <span className="hidden sm:inline">Comparables</span>
              <span className="sm:hidden">Comps</span>
            </TabsTrigger>
            <TabsTrigger value="exit" className="gap-1 whitespace-nowrap">
              <ExitIcon size={16} />
              Sortie
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-1 whitespace-nowrap">
              <NotePencil size={16} />
              <span className="hidden sm:inline">Notes & Historique</span>
              <span className="sm:hidden">Notes</span>
            </TabsTrigger>
            <TabsTrigger value="partners" className="gap-1 whitespace-nowrap">
              <Users size={16} />
              Partenaires
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ===== SECTION 3: DEAL ANALYZER SUMMARY (READ-ONLY) ===== */}
        <TabsContent value="analyzer" className="space-y-6" data-testid="analyzer-section">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator size={20} />
                    Résumé de l'Analyseur
                  </CardTitle>
                  <CardDescription>
                    Résultats en lecture seule du moteur de calcul
                  </CardDescription>
                </div>
                <Button onClick={runAnalyzer} disabled={analyzing} size="sm">
                  {analyzing ? <ArrowsClockwise size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />}
                  <span className="ml-1">Actualiser</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Cost Breakdown */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Détail des Coûts</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Acquisition Totale</p>
                    <p className="text-xl font-bold font-tabular">{formatCurrency(deal.total_acquisition_cost)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Rénovation</p>
                    <p className="text-xl font-bold font-tabular">{formatCurrency(deal.renovation?.renovation_budget * (1 + (deal.renovation?.contingency_percent || 0) / 100) + (deal.renovation?.design_styling_budget || 0) + (deal.renovation?.project_management_fee || 0))}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Coût Total Projet</p>
                    <p className="text-xl font-bold font-tabular">{formatCurrency(deal.total_project_cost)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Coût par m²</p>
                    <p className="text-xl font-bold font-tabular">{formatPricePerSqm(deal.cost_per_sqm)}</p>
                  </div>
                </div>
              </div>

              {/* Returns */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Rendements Attendus</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Profit Brut</p>
                    <p className={cn("text-xl font-bold font-tabular", getProfitColorClass(deal.expected_gross_profit))}>
                      {formatCurrency(deal.expected_gross_profit)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Profit Net</p>
                    <p className={cn("text-xl font-bold font-tabular", getProfitColorClass(deal.expected_net_profit))}>
                      {formatCurrency(deal.expected_net_profit)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Rendement</p>
                    <p className={cn("text-xl font-bold font-tabular", getRoiColorClass(deal.expected_roi_percent))}>
                      {formatPercent(deal.expected_roi_percent)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Gross Margin</p>
                    <p className="text-xl font-bold font-tabular">{formatPercent(deal.expected_gross_margin_percent)}</p>
                  </div>
                </div>
              </div>

              {/* Bid Strategy */}
              <div>
                <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Bid Strategy</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-xs text-muted-foreground">Max Bid @ {deal.target_roi_percent}% ROI</p>
                    <p className="text-xl font-bold font-tabular text-primary">{formatCurrency(deal.max_bid_at_target_roi)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Break-even Exit</p>
                    <p className="text-xl font-bold font-tabular">{formatCurrency(deal.break_even_exit_price)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Break-even/m²</p>
                    <p className="text-xl font-bold font-tabular">{formatPricePerSqm(deal.break_even_exit_price_per_sqm)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Target Exit</p>
                    <p className="text-xl font-bold font-tabular">{formatCurrency(deal.exit_assumptions?.target_sale_price)}</p>
                  </div>
                </div>
              </div>

              {/* Scenarios (if available) */}
              {deal.scenarios && deal.scenarios.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">Scenario Analysis</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {deal.scenarios.map((scenario) => (
                      <div 
                        key={scenario.id}
                        className={cn(
                          'p-4 rounded-lg border',
                          scenario.scenario_type === 'base_case' && 'border-primary bg-primary/5'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold">{scenario.name}</h5>
                          {scenario.scenario_type === 'base_case' && <Badge>Base</Badge>}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Exit Price</span>
                            <span className="font-tabular">{formatCurrency(scenario.exit_price)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Cost</span>
                            <span className="font-tabular">{formatCurrency(scenario.total_project_cost)}</span>
                          </div>
                          <div className="flex justify-between font-medium pt-1 border-t">
                            <span>ROI</span>
                            <span className={cn('font-tabular', getRoiColorClass(scenario.roi_percent))}>
                              {formatPercent(scenario.roi_percent)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SECTION 3.5: AI INTELLIGENCE ===== */}
        <TabsContent value="intelligence" className="space-y-6" data-testid="intelligence-section">
          <DealAIIntelligenceSummary dealId={dealId} />
        </TabsContent>

        {/* ===== SECTION 4: ASSUMPTIONS (EDITABLE) ===== */}
        <TabsContent value="assumptions" className="space-y-6" data-testid="assumptions-section">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target size={20} />
                    Deal Assumptions
                  </CardTitle>
                  <CardDescription>
                    Edit assumptions and recalculate deal metrics
                  </CardDescription>
                </div>
                {!editingAssumptions ? (
                  <Button onClick={() => setEditingAssumptions(true)} variant="outline">
                    <PencilSimple size={16} className="mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditingAssumptions(false)}>
                      <X size={16} className="mr-1" />
                      Annuler
                    </Button>
                    <Button onClick={saveAssumptions} disabled={saving}>
                      {saving ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : <FloppyDisk size={16} className="mr-1" />}
                      Enregistrer & Recalculer
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Acquisition Assumptions */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CurrencyEur size={18} />
                    Acquisition
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label>Prix d'Achat Cible</Label>
                      <Input
                        type="number"
                        value={assumptions?.target_purchase_price || ''}
                        onChange={(e) => setAssumptions({...assumptions, target_purchase_price: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Notary Costs (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={assumptions?.notary_costs_percent || ''}
                        onChange={(e) => setAssumptions({...assumptions, notary_costs_percent: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Agency Fee (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={assumptions?.agency_fee_percent || ''}
                        onChange={(e) => setAssumptions({...assumptions, agency_fee_percent: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                  </div>
                </div>

                {/* Renovation Assumptions */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Wrench size={18} />
                    Renovation
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label>Base Renovation Budget</Label>
                      <Input
                        type="number"
                        value={assumptions?.renovation_budget || ''}
                        onChange={(e) => setAssumptions({...assumptions, renovation_budget: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Contingency (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={assumptions?.contingency_percent || ''}
                        onChange={(e) => setAssumptions({...assumptions, contingency_percent: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Design & Styling Budget</Label>
                      <Input
                        type="number"
                        value={assumptions?.design_styling_budget || ''}
                        onChange={(e) => setAssumptions({...assumptions, design_styling_budget: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Project Management Fee</Label>
                      <Input
                        type="number"
                        value={assumptions?.project_management_fee || ''}
                        onChange={(e) => setAssumptions({...assumptions, project_management_fee: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                  </div>
                </div>

                {/* Exit & Financing Assumptions */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <ExitIcon size={18} />
                    Exit & Financing
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label>Target Sale Price</Label>
                      <Input
                        type="number"
                        value={assumptions?.target_sale_price || ''}
                        onChange={(e) => setAssumptions({...assumptions, target_sale_price: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Sales Fee (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={assumptions?.expected_sales_fee_percent || ''}
                        onChange={(e) => setAssumptions({...assumptions, expected_sales_fee_percent: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Holding Period (months)</Label>
                      <Input
                        type="number"
                        value={assumptions?.holding_period_months || ''}
                        onChange={(e) => setAssumptions({...assumptions, holding_period_months: parseInt(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Interest Rate (% annual)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={assumptions?.interest_rate_annual || ''}
                        onChange={(e) => setAssumptions({...assumptions, interest_rate_annual: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                    <div>
                      <Label>Target ROI (%)</Label>
                      <Input
                        type="number"
                        step="1"
                        value={assumptions?.target_roi_percent || ''}
                        onChange={(e) => setAssumptions({...assumptions, target_roi_percent: parseFloat(e.target.value) || 0})}
                        disabled={!editingAssumptions}
                        className="font-tabular"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SECTION 5: RENOVATION TRACKING ===== */}
        <TabsContent value="renovation" className="space-y-6" data-testid="renovation-section">
          {/* Quick Variance Summary + Link to Full Control */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Planned Budget</p>
                    <p className="text-lg font-bold font-tabular">
                      {formatCurrency(deal.renovation?.renovation_budget * (1 + (deal.renovation?.contingency_percent || 0) / 100) + (deal.renovation?.design_styling_budget || 0) + (deal.renovation?.project_management_fee || 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Actual Spend</p>
                    <p className="text-lg font-bold font-tabular">
                      {deal.renovation?.actual_renovation_spend ? formatCurrency(deal.renovation.actual_renovation_spend) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Status</p>
                    <Badge variant="secondary" className={cn(
                      "capitalize",
                      deal.renovation_status === 'completed' && 'bg-emerald-100 text-emerald-700',
                      deal.renovation_status === 'in_progress' && 'bg-blue-100 text-blue-700'
                    )}>
                      {deal.renovation_status === 'in_progress' ? 'In Progress' : 
                       deal.renovation_status === 'completed' ? 'Completed' : 'Not Started'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Timeline</p>
                    <Badge variant={deal.timeline_status === 'delayed' ? 'destructive' : 'secondary'} className="capitalize">
                      {deal.timeline_status === 'delayed' ? 'Delayed' :
                       deal.timeline_status === 'ahead' ? 'Ahead' : 'On Track'}
                    </Badge>
                  </div>
                </div>
                <Link to={`/deal/${dealId}/renovation`}>
                  <Button className="bg-[#3A4A40] hover:bg-[#2E3B33]" data-testid="open-renovation-control">
                    <Wrench size={16} className="mr-1" />
                    Open Renovation Control
                    <CaretRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench size={20} />
                  Renovation Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="secondary" className="capitalize">
                    {deal.renovation_status || 'Not Started'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Timeline</span>
                  <Badge variant={deal.timeline_status === 'delayed' ? 'destructive' : 'secondary'} className="capitalize">
                    {deal.timeline_status || 'On Track'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium capitalize">{deal.renovation?.renovation_type?.replace('_', ' ')}</span>
                </div>
                <hr />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Budget</span>
                  <span className="font-tabular font-medium">{formatCurrency(deal.renovation?.renovation_budget)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Contingency ({deal.renovation?.contingency_percent}%)</span>
                  <span className="font-tabular">{formatCurrency(deal.renovation?.renovation_budget * (deal.renovation?.contingency_percent / 100))}</span>
                </div>
                {deal.renovation?.actual_renovation_spend && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Actual Spend</span>
                    <span className={cn(
                      'font-tabular font-medium',
                      deal.renovation.actual_renovation_spend > deal.renovation.renovation_budget * 1.1 ? 'text-red-600' : 'text-emerald-600'
                    )}>
                      {formatCurrency(deal.renovation.actual_renovation_spend)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Milestones */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Clock size={20} />
                    Project Milestones
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {completedMilestones}/{totalMilestones}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {deal.milestones && deal.milestones.length > 0 ? (
                  <div className="space-y-3">
                    <Progress value={milestoneProgress} className="h-2" />
                    {deal.milestones.map((milestone, idx) => (
                      <div 
                        key={milestone.id || idx}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30"
                      >
                        {milestone.completed ? (
                          <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Clock size={20} className="text-muted-foreground" />
                        )}
                        <span className={cn('flex-1', milestone.completed && 'text-muted-foreground')}>
                          {milestone.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(milestone.actual_date || milestone.target_date)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">No milestones defined</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== SECTION 6: MARKET COMPARABLES INTELLIGENCE ===== */}
        <TabsContent value="comparables" className="space-y-6" data-testid="comparables-section">
          <DealComparablesIntelligence dealId={dealId} />
        </TabsContent>

        {/* ===== SECTION 6.5: EXIT INTELLIGENCE ===== */}
        <TabsContent value="exit" className="space-y-6" data-testid="exit-section">
          <DealExitIntelligence dealId={dealId} />
        </TabsContent>

        {/* ===== SECTION 7: NOTES & HISTORY ===== */}
        <TabsContent value="notes" className="space-y-6" data-testid="notes-section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <NotePencil size={20} />
                  Deal Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deal.notes ? (
                  <p className="text-muted-foreground whitespace-pre-wrap">{deal.notes}</p>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">No notes added</p>
                )}
              </CardContent>
            </Card>

            {/* Risk Flags */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Warning size={20} />
                  Risk Flags
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deal.risk_flags && deal.risk_flags.length > 0 ? (
                  <div className="space-y-3">
                    {deal.risk_flags.map((risk, idx) => (
                      <div 
                        key={risk.id || idx}
                        className={cn(
                          'p-3 rounded-lg border-l-4',
                          getSeverityColor(risk.severity)
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="capitalize text-xs">
                            {risk.severity}
                          </Badge>
                          <span className="text-xs text-muted-foreground capitalize">
                            {risk.risk_type.replace('_', ' ')}
                          </span>
                          {risk.resolved && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Resolved</Badge>
                          )}
                        </div>
                        <p className="text-sm">{risk.description}</p>
                        {risk.mitigation && (
                          <p className="text-xs text-muted-foreground mt-1">
                            <strong>Mitigation:</strong> {risk.mitigation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
                    <CheckCircle size={20} className="text-emerald-600" />
                    <span>No risk flags</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== SECTION 8: PARTNER TRACKING WITH SCORECARD ===== */}
        <TabsContent value="partners" className="space-y-6" data-testid="partners-section">
          <DealPartnerIntelligence dealId={dealId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DealDetail;
